
import { ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'

type Audience = 'manager' | 'employee'

export function RoleGate({ audience, children }: { audience: Audience; children: ReactNode }) {
  const location = useLocation()
  const [state, setState] = useState<'loading' | 'signed-out' | 'no-membership' | 'manager' | 'employee'>('loading')

  useEffect(() => {
    let alive = true

    async function check() {
      if (!supabase) {
        if (alive) setState('signed-out')
        return
      }

      const client = supabase
      const { data: { session } } = await client.auth.getSession()
      if (!session) {
        if (alive) setState('signed-out')
        return
      }

      const { data, error } = await client
        .from('memberships')
        .select('role')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle()

      if (!alive) return

      if (error || !data?.role) {
        setState('no-membership')
        return
      }

      setState(data.role === 'member' ? 'employee' : 'manager')
    }

    check()
    if (!supabase) return

    const { data: listener } = supabase.auth.onAuthStateChange(() => check())

    return () => {
      alive = false
      listener.subscription.unsubscribe()
    }
  }, [])

  if (state === 'loading') {
    return <div className="auth-loading" aria-label="Loading workspace"><span className="blue-spinner auth-spinner"/></div>
  }

  if (state === 'signed-out') {
    return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />
  }

  if (state === 'no-membership') {
    return <Navigate to="/onboarding" replace />
  }

  if (audience === 'employee' && state === 'manager') {
    return <Navigate to="/app" replace />
  }

  if (audience === 'manager' && state === 'employee') {
    return <Navigate to="/employee" replace />
  }

  return <>{children}</>
}
